import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Briefcase, Search, X, MapPin, UsersRound,
  CalendarDays, Clock, ChevronLeft, ChevronRight,
  Eye, Trash2, CheckCircle2, FileText, Image,
  MessageSquare, RefreshCw, AlertTriangle,
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

// Polygon centroid for label placement
const centroid = (pts) => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
});

const svgPts = (pts) => pts.map(p => `${p.x},${p.y}`).join(' ');

// ─── Street View Photo Viewer ────────────────────────────────────────────────
const StreetViewViewer = ({ photos, baseUrl }) => {
  const [filter,        setFilter]        = useState('all');
  const [current,       setCurrent]       = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [zoomOrigin,    setZoomOrigin]    = useState('50% 70%');
  const [imgKey,        setImgKey]        = useState(0);
  const [hoveredNode,   setHoveredNode]   = useState(null);
  const stripRef = useRef(null);

  const filtered = filter === 'all' ? photos : photos.filter(p => p.photo_type === filter);

  useEffect(() => { setCurrent(0); setImgKey(k => k + 1); }, [filter]);

  useEffect(() => {
    if (!stripRef.current) return;
    const thumb = stripRef.current.querySelector(`.sv-thumb-${current}`);
    if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [current]);

  const navigateTo = (nextIdx, originPercent) => {
    if (nextIdx < 0 || nextIdx >= filtered.length || transitioning) return;
    setZoomOrigin(originPercent);
    setTransitioning(true);
    setTimeout(() => { setCurrent(nextIdx); setImgKey(k => k + 1); setTransitioning(false); }, 320);
  };

  if (photos.length === 0) return (
    <div className="md-empty"><div className="md-empty-icon"><Image size={24}/></div>No photos uploaded yet</div>
  );

  const beforeCount = photos.filter(p => p.photo_type === 'before').length;
  const afterCount  = photos.filter(p => p.photo_type === 'after').length;
  const photo       = filtered[current];
  const prevPhoto   = filtered[current - 1];
  const nextPhoto   = filtered[current + 1];

  if (filtered.length === 0) return (
    <div className="md-empty"><div className="md-empty-icon"><Image size={24}/></div>No {filter} photos</div>
  );

  return (
    <div>
      <div className="sv-toggle-row">
        {[
          { key: 'before', label: `Before (${beforeCount})` },
          { key: 'all',    label: `All (${photos.length})`  },
          { key: 'after',  label: `After (${afterCount})`   },
        ].map(f => (
          <button key={f.key} className={`sv-toggle-btn${filter === f.key ? ' active' : ''} sv-toggle-${f.key}`}
            onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>
      <div className="sv-main">
        <img key={imgKey} src={`${baseUrl}${photo.photo_url}`} alt={photo.point_label} draggable={false}
          className={`sv-photo ${transitioning ? 'sv-zoom-out' : 'sv-zoom-in'}`}
          style={{ transformOrigin: zoomOrigin }}/>
        {current > 0 && (
          <div className={`sv-node sv-node-prev${hoveredNode === 'prev' ? ' sv-node-hovered' : ''}`}
            onMouseEnter={() => setHoveredNode('prev')} onMouseLeave={() => setHoveredNode(null)}
            onClick={() => navigateTo(current - 1, '20% 70%')}>
            {hoveredNode === 'prev' && <div className="sv-node-tooltip sv-node-tooltip-right">← {prevPhoto?.point_label || `Photo ${current}`}</div>}
            <div className="sv-node-ring"><ChevronLeft size={20} strokeWidth={2.5}/></div>
            <div className="sv-node-shadow"/>
          </div>
        )}
        {current < filtered.length - 1 && (
          <div className={`sv-node sv-node-next${hoveredNode === 'next' ? ' sv-node-hovered' : ''}`}
            onMouseEnter={() => setHoveredNode('next')} onMouseLeave={() => setHoveredNode(null)}
            onClick={() => navigateTo(current + 1, '65% 70%')}>
            {hoveredNode === 'next' && <div className="sv-node-tooltip sv-node-tooltip-left">{nextPhoto?.point_label || `Photo ${current + 2}`} →</div>}
            <div className="sv-node-ring"><ChevronRight size={20} strokeWidth={2.5}/></div>
            <div className="sv-node-shadow"/>
          </div>
        )}
        <div className="sv-label">
          <span className="sv-label-point">{photo.point_label}</span>
          <span className={`sv-label-type sv-label-type-${photo.photo_type}`}>{photo.photo_type.toUpperCase()}</span>
        </div>
        <div className="sv-counter">{current + 1} / {filtered.length}</div>
      </div>
      <div className="sv-strip" ref={stripRef}>
        {filtered.map((p, i) => (
          <img key={p.id} src={`${baseUrl}${p.photo_url}`} alt={p.point_label}
            className={`sv-thumb sv-thumb-${i}${i === current ? ' active' : ''} sv-thumb-${p.photo_type}`}
            onClick={() => navigateTo(i, '50% 50%')}/>
        ))}
      </div>
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────
const STYLES = `
  .mj-toolbar { display:flex; align-items:center; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
  .mj-search-wrap { position:relative; flex:1; max-width:320px; }
  .mj-search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:rgba(255,255,255,0.25); pointer-events:none; }
  .mj-search {
    width:100%; padding:9px 14px 9px 38px;
    background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08);
    border-radius:10px; color:#E2E8F0;
    font-family:'DM Sans',sans-serif; font-size:13px;
    outline:none; transition:border-color 0.2s; box-sizing:border-box;
  }
  .mj-search:focus { border-color:rgba(14,165,233,0.35); }
  .mj-search::placeholder { color:rgba(255,255,255,0.20); }
  .mj-filter-select {
    padding:9px 14px; background:rgba(255,255,255,0.05);
    border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E2E8F0;
    font-family:'DM Sans',sans-serif; font-size:13px; outline:none; cursor:pointer;
  }
  .mj-filter-select option { background:#1E2535; }

  .mj-stats-strip { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
  .mj-stat-chip { display:flex; align-items:center; gap:7px; padding:8px 14px; border-radius:10px; font-size:12px; font-weight:700; font-family:'DM Mono',monospace; border:1px solid; }
  .mj-stat-chip-val { font-size:18px; font-weight:800; letter-spacing:-0.03em; }

  .mj-table-wrap { background:#0D1120; border:1px solid rgba(255,255,255,0.06); border-radius:14px; overflow:hidden; margin-bottom:20px; }
  .mj-status { display:inline-flex; align-items:center; gap:5px; padding:3px 10px; border-radius:20px; font-size:10px; font-weight:700; font-family:'DM Mono',monospace; }
  .mj-status-dot { width:5px; height:5px; border-radius:50%; }
  .mj-status-scheduled   { background:rgba(14,165,233,0.12);  color:#38BDF8; }  .mj-status-scheduled .mj-status-dot   { background:#38BDF8; }
  .mj-status-in_progress { background:rgba(245,158,11,0.12);  color:#FCD34D; }  .mj-status-in_progress .mj-status-dot { background:#FCD34D; }
  .mj-status-completed   { background:rgba(34,197,94,0.12);   color:#86EFAC; }  .mj-status-completed .mj-status-dot   { background:#86EFAC; }
  .mj-status-cancelled   { background:rgba(239,68,68,0.10);   color:#FCA5A5; }  .mj-status-cancelled .mj-status-dot   { background:#FCA5A5; }
  .mj-row-actions { display:flex; align-items:center; gap:6px; opacity:0; transition:opacity 0.15s; }
  tr:hover .mj-row-actions { opacity:1; }

  .mj-panel-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.60); backdrop-filter:blur(5px); z-index:200; }
  .mj-panel {
    position:fixed; top:0; right:0; bottom:0; width:640px; max-width:100vw;
    background:#0D1120; border-left:1px solid rgba(255,255,255,0.08);
    box-shadow:-24px 0 80px rgba(0,0,0,0.55);
    z-index:201; display:flex; flex-direction:column;
    animation:slideIn 0.22s cubic-bezier(0.4,0,0.2,1); overflow:hidden;
  }
  @keyframes slideIn { from{transform:translateX(100%);opacity:0;} to{transform:translateX(0);opacity:1;} }
  .mj-panel-header { padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.06); display:flex; align-items:center; gap:12px; flex-shrink:0; }
  .mj-panel-title { font-size:16px; font-weight:800; color:#F1F5F9; flex:1; }
  .mj-panel-sub   { font-size:11px; color:rgba(255,255,255,0.28); margin-top:2px; }
  .mj-panel-tabs  { display:flex; gap:4px; padding:0 24px; border-bottom:1px solid rgba(255,255,255,0.06); flex-shrink:0; overflow-x:auto; }
  .mj-panel-tab   { padding:12px 14px; font-size:12px; font-weight:700; color:rgba(255,255,255,0.35); cursor:pointer; border:none; background:none; white-space:nowrap; border-bottom:2px solid transparent; transition:all 0.18s; font-family:'DM Sans',sans-serif; }
  .mj-panel-tab:hover { color:rgba(255,255,255,0.65); }
  .mj-panel-tab.active { color:#0EA5E9; border-bottom-color:#0EA5E9; }
  .mj-panel-body { flex:1; overflow-y:auto; padding:22px 24px; }

  .mj-info-row { display:flex; align-items:flex-start; gap:10px; padding:11px 0; border-bottom:1px solid rgba(255,255,255,0.04); font-size:13px; color:rgba(255,255,255,0.55); }
  .mj-info-row:last-child { border-bottom:none; }
  .mj-info-label { width:100px; flex-shrink:0; font-size:9px; font-weight:700; color:rgba(255,255,255,0.22); font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; padding-top:1px; }

  /* String flags */
  .mj-string-flags-section { margin-top:24px; padding-top:20px; border-top:1px solid rgba(255,255,255,0.06); }
  .mj-string-flag-chip { display:inline-flex; align-items:center; gap:7px; padding:7px 14px; border-radius:20px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; cursor:pointer; transition:all 0.18s; border:1.5px solid; }
  .mj-string-flag-chip:hover { filter:brightness(1.15); }
  .mj-flag-warning-bar { display:flex; align-items:center; gap:8px; padding:9px 13px; border-radius:9px; background:rgba(239,68,68,0.08); border:1px solid rgba(239,68,68,0.20); margin-bottom:14px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; color:#FCA5A5; }

  /* Diagram */
  .mj-diagram-wrap { position:relative; border-radius:10px; overflow:hidden; border:1px solid rgba(14,165,233,0.18); margin-bottom:16px; }
  .mj-diagram-img  { width:100%; display:block; user-select:none; }

  /* SVG overlay for polygon strings */
  .mj-svg-overlay { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible; }

  /* String centroid labels */
  .mj-string-centroid-label {
    position:absolute; transform:translate(-50%,-50%);
    font-size:9px; font-weight:800; font-family:'DM Mono',monospace;
    border-radius:4px; padding:2px 7px; white-space:nowrap;
    background:rgba(0,0,0,0.70); backdrop-filter:blur(4px);
    pointer-events:none; z-index:10;
  }

  /* Flagged string pulse */
  @keyframes flagPulse { 0%,100%{opacity:0.80;} 50%{opacity:1;} }
  .mj-string-flagged-polygon { animation:flagPulse 2s ease-in-out infinite; }

  /* Pins */
  .mj-diagram-pin { position:absolute; transform:translate(-50%,-50%); cursor:pointer; z-index:10; display:flex; flex-direction:column; align-items:center; gap:3px; }
  .mj-pin-dot { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:800; font-family:'DM Mono',monospace; box-shadow:0 0 0 3px rgba(0,0,0,0.40),0 4px 12px rgba(0,0,0,0.50); transition:transform 0.15s; color:#080C18; }
  .mj-diagram-pin:hover .mj-pin-dot { transform:scale(1.18); }
  .mj-pin-label-tag { background:rgba(8,12,24,0.85); border-radius:4px; padding:2px 6px; font-size:9px; font-weight:700; font-family:'DM Mono',monospace; color:#E2E8F0; white-space:nowrap; backdrop-filter:blur(4px); pointer-events:none; border:1px solid rgba(255,255,255,0.08); }
  .mj-pin-has-note  { background:#F59E0B; }
  .mj-pin-has-photo { background:#22D3A5; }
  .mj-pin-has-annot { background:#EF4444; color:#fff; }
  .mj-pin-empty     { background:rgba(255,255,255,0.28); color:rgba(0,0,0,0.55); }

  .mj-annot-item { padding:14px; border-radius:10px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); margin-bottom:8px; }
  .mj-annot-header { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
  .mj-annot-type-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
  .mj-annot-point { font-size:10px; font-weight:700; color:rgba(255,255,255,0.40); font-family:'DM Mono',monospace; flex:1; }
  .mj-annot-fo    { font-size:10px; color:rgba(255,255,255,0.25); font-family:'DM Mono',monospace; }
  .mj-annot-text  { font-size:13px; color:rgba(255,255,255,0.65); line-height:1.55; margin-bottom:8px; }
  .mj-annot-photo { width:100%; border-radius:8px; object-fit:cover; max-height:200px; border:1px solid rgba(255,255,255,0.06); }

  .mj-fsr-card { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:12px; padding:18px; margin-bottom:16px; }
  .mj-fsr-sig  { width:100%; border-radius:8px; border:1px solid rgba(255,255,255,0.06); background:#fff; margin-top:10px; max-height:120px; object-fit:contain; }
  .mj-fsr-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-bottom:16px; }
  .mj-fsr-field { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:8px; padding:10px 12px; }
  .mj-fsr-field-label { font-size:9px; font-weight:700; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.12em; color:rgba(255,255,255,0.22); margin-bottom:4px; }
  .mj-fsr-field-val { font-size:13px; font-weight:600; color:#E2E8F0; }
  .mj-fsr-bool-yes { color:#86EFAC; } .mj-fsr-bool-no { color:#FCA5A5; }

  .mj-pagination { display:flex; align-items:center; justify-content:center; gap:8px; padding:16px 0; }
  .mj-page-btn { width:32px; height:32px; border-radius:8px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); color:rgba(255,255,255,0.45); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; transition:all 0.18s; font-family:'DM Mono',monospace; }
  .mj-page-btn:hover { background:rgba(14,165,233,0.10); color:#38BDF8; }
  .mj-page-btn.active { background:linear-gradient(135deg,rgba(14,165,233,0.20),rgba(245,158,11,0.12)); color:#E2E8F0; border-color:rgba(14,165,233,0.25); }
  .mj-page-info { font-size:11px; color:rgba(255,255,255,0.28); font-family:'DM Mono',monospace; }

  /* Street view */
  .sv-toggle-row { display:flex; gap:6px; margin-bottom:14px; }
  .sv-toggle-btn { padding:6px 16px; border-radius:20px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; cursor:pointer; border:1px solid rgba(255,255,255,0.08); background:rgba(255,255,255,0.04); color:rgba(255,255,255,0.35); transition:all 0.18s; }
  .sv-toggle-btn.active.sv-toggle-before { background:rgba(56,189,248,0.15); color:#38BDF8; border-color:rgba(56,189,248,0.30); }
  .sv-toggle-btn.active.sv-toggle-all    { background:rgba(255,255,255,0.08); color:#E2E8F0; border-color:rgba(255,255,255,0.20); }
  .sv-toggle-btn.active.sv-toggle-after  { background:rgba(134,239,172,0.15); color:#86EFAC; border-color:rgba(134,239,172,0.30); }
  .sv-main { position:relative; border-radius:12px; overflow:hidden; background:#000; height:340px; margin-bottom:10px; }
  .sv-photo { width:100%; height:100%; object-fit:cover; display:block; will-change:transform,opacity; }
  @keyframes svZoomIn  { from{transform:scale(1.14);opacity:0;} to{transform:scale(1.00);opacity:1;} }
  @keyframes svZoomOut { from{transform:scale(1.00);opacity:1;} to{transform:scale(1.14);opacity:0;} }
  .sv-zoom-in  { animation:svZoomIn  0.38s cubic-bezier(0.25,0.46,0.45,0.94) forwards; }
  .sv-zoom-out { animation:svZoomOut 0.30s cubic-bezier(0.55,0,1,0.45) forwards; }
  .sv-node { position:absolute; display:flex; flex-direction:column; align-items:center; cursor:pointer; z-index:20; transition:transform 0.2s; user-select:none; }
  .sv-node-prev { left:18%; bottom:22%; transform:translateX(-50%); }
  .sv-node-next { left:62%; bottom:22%; transform:translateX(-50%); }
  .sv-node:hover { transform:translateX(-50%) scale(1.12); }
  .sv-node-ring { width:50px; height:50px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,0.18); border:2px solid rgba(255,255,255,0.75); color:#fff; backdrop-filter:blur(8px); box-shadow:0 4px 20px rgba(0,0,0,0.55),0 0 0 4px rgba(0,0,0,0.20); transition:background 0.2s,border-color 0.2s; }
  .sv-node-hovered .sv-node-ring { background:rgba(255,255,255,0.30); border-color:#fff; }
  .sv-node-shadow { width:36px; height:8px; margin-top:4px; background:rgba(0,0,0,0.45); border-radius:50%; filter:blur(4px); }
  .sv-node-tooltip { position:absolute; bottom:68px; background:rgba(8,12,24,0.88); border:1px solid rgba(255,255,255,0.12); backdrop-filter:blur(8px); border-radius:8px; padding:6px 12px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; color:#E2E8F0; white-space:nowrap; pointer-events:none; animation:fadeInUp 0.15s ease; }
  .sv-node-tooltip-left,.sv-node-tooltip-right { left:50%; transform:translateX(-50%); }
  @keyframes fadeInUp { from{opacity:0;transform:translateX(-50%) translateY(6px);} to{opacity:1;transform:translateX(-50%) translateY(0);} }
  .sv-label { position:absolute; bottom:12px; left:12px; display:flex; align-items:center; gap:8px; background:rgba(0,0,0,0.68); backdrop-filter:blur(8px); border-radius:8px; padding:6px 12px; }
  .sv-label-point { font-size:12px; font-weight:700; color:#E2E8F0; }
  .sv-label-type-before { font-size:9px; font-weight:800; font-family:'DM Mono'; color:#38BDF8; letter-spacing:0.08em; }
  .sv-label-type-after  { font-size:9px; font-weight:800; font-family:'DM Mono'; color:#86EFAC; letter-spacing:0.08em; }
  .sv-counter { position:absolute; bottom:12px; right:12px; font-size:11px; font-weight:700; font-family:'DM Mono',monospace; color:rgba(255,255,255,0.60); background:rgba(0,0,0,0.60); border-radius:6px; padding:4px 9px; backdrop-filter:blur(4px); }
  .sv-strip { display:flex; gap:6px; overflow-x:auto; padding:4px 0 12px; scrollbar-width:none; }
  .sv-strip::-webkit-scrollbar { display:none; }
  .sv-thumb { width:68px; height:51px; border-radius:7px; object-fit:cover; cursor:pointer; flex-shrink:0; border:2px solid transparent; opacity:0.55; transition:all 0.15s; }
  .sv-thumb:hover { opacity:0.80; }
  .sv-thumb.active { opacity:1; border-color:#0EA5E9; }
  .sv-thumb.sv-thumb-before.active { border-color:#38BDF8; }
  .sv-thumb.sv-thumb-after.active  { border-color:#86EFAC; }
`;

const PAGE_SIZE = 12;

const StatusBadge = ({ status }) => (
  <span className={`mj-status mj-status-${status}`}>
    <span className="mj-status-dot"/>
    {status.replace('_', ' ')}
  </span>
);

const FsrBool = ({ label, value }) => (
  <div className="mj-fsr-field">
    <div className="mj-fsr-field-label">{label}</div>
    <div className={`mj-fsr-field-val ${value ? 'mj-fsr-bool-yes' : 'mj-fsr-bool-no'}`}>
      {value ? '✓ Yes' : '✗ No'}
    </div>
  </div>
);

const MideastJobs = () => {
  const [jobs,         setJobs]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMonth,  setFilterMonth]  = useState('');
  const [page,         setPage]         = useState(1);

  const [selected,     setSelected]     = useState(null);
  const [detail,       setDetail]       = useState(null);
  const [detLoading,   setDetLoading]   = useState(false);
  const [panelTab,     setPanelTab]     = useState('info');

  const [layerNotes,   setLayerNotes]   = useState(true);
  const [layerPhotos,  setLayerPhotos]  = useState(true);
  const [layerAnnots,  setLayerAnnots]  = useState(true);

  const [photos,       setPhotos]       = useState([]);
  const [annotations,  setAnnotations]  = useState([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  // String flags
  const [jobStrings,         setJobStrings]         = useState([]);
  const [jobStringFlags,     setJobStringFlags]     = useState([]);
  const [jobStringFlagNotes, setJobStringFlagNotes] = useState({});
  const [savingFlags,        setSavingFlags]        = useState(false);
  const [flagsSaved,         setFlagsSaved]         = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BASE}/mideast/jobs?`;
      if (filterMonth) url += `month=${filterMonth}&`;
      const res = await axios.get(url, getAuthHeader());
      setJobs(res.data.jobs || []);
      setPage(1);
    } catch (err) {
      console.error('[Jobs] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const openDetail = useCallback(async (job) => {
    setSelected(job);
    setDetail(null);
    setPanelTab('info');
    setPhotos([]);
    setAnnotations([]);
    setPhotosLoaded(false);
    setJobStrings([]);
    setJobStringFlags([]);
    setJobStringFlagNotes({});
    setFlagsSaved(false);
    setDetLoading(true);
    try {
      const [jobRes, fsrRes] = await Promise.all([
        axios.get(`${BASE}/mideast/jobs/${job.id}`,     getAuthHeader()),
        axios.get(`${BASE}/mideast/fsrs/job/${job.id}`, getAuthHeader()),
      ]);
      const jobDetail = { ...jobRes.data, fsr: fsrRes.data.fsr || null };
      setDetail(jobDetail);

      if (jobDetail.diagram?.id) {
        const [stringsSettled, flagsSettled] = await Promise.allSettled([
          axios.get(`${BASE}/mideast/strings/diagram/${jobDetail.diagram.id}`, getAuthHeader()),
          axios.get(`${BASE}/mideast/strings/job/${job.id}/flags`,             getAuthHeader()),
        ]);
        if (stringsSettled.status === 'fulfilled') setJobStrings(stringsSettled.value.data.strings || []);
        if (flagsSettled.status === 'fulfilled') {
          const flags = flagsSettled.value.data.flags || [];
          setJobStringFlags(flags.map(f => f.string_id));
          setJobStringFlagNotes(Object.fromEntries(flags.map(f => [f.string_id, f.note || ''])));
        }
      }
    } catch (err) {
      console.error('[Jobs] detail error:', err);
    } finally {
      setDetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (panelTab !== 'visual' || !selected || photosLoaded) return;
    Promise.all([
      axios.get(`${BASE}/mideast/point-photos/job/${selected.id}`,   getAuthHeader()),
      axios.get(`${BASE}/mideast/fo-annotations/job/${selected.id}`, getAuthHeader()),
    ]).then(([photosRes, annotRes]) => {
      setPhotos(photosRes.data.photos           || []);
      setAnnotations(annotRes.data.annotations  || []);
      setPhotosLoaded(true);
    }).catch(err => console.error('[Jobs] photos error:', err));
  }, [panelTab, selected, photosLoaded]);

  const handleDelete = async (job, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete job at ${job.site_name}?`)) return;
    try {
      await axios.delete(`${BASE}/mideast/jobs/${job.id}`, getAuthHeader());
      fetchJobs();
      if (selected?.id === job.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Delete failed');
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${BASE}/mideast/jobs/${selected.id}/status`, { status }, getAuthHeader());
      setSelected(j => ({ ...j, status }));
      setDetail(d => ({ ...d, job: { ...d.job, status } }));
      fetchJobs();
    } catch (err) {
      console.error('[Jobs] status error:', err);
    }
  };

  const toggleStringFlag = (stringId) => {
    setFlagsSaved(false);
    setJobStringFlags(prev => prev.includes(stringId) ? prev.filter(id => id !== stringId) : [...prev, stringId]);
  };

  const saveStringFlags = async () => {
    if (!selected) return;
    setSavingFlags(true);
    try {
      await axios.post(
        `${BASE}/mideast/strings/job/${selected.id}/flags`,
        { flags: jobStringFlags.map(sid => ({ string_id: sid, note: jobStringFlagNotes[sid] || '' })) },
        getAuthHeader()
      );
      setFlagsSaved(true);
    } catch (err) {
      alert('Failed to save attention flags');
    } finally {
      setSavingFlags(false);
    }
  };

  const filtered = jobs.filter(j => {
    const q = search.toLowerCase();
    const matchSearch = j.site_name?.toLowerCase().includes(q) || j.client_name?.toLowerCase().includes(q) || j.team_name?.toLowerCase().includes(q);
    return matchSearch && (!filterStatus || j.status === filterStatus);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = {
    total:      jobs.length,
    scheduled:  jobs.filter(j => j.status === 'scheduled').length,
    inProgress: jobs.filter(j => ['in_progress','reached_site','reached_panels','work_started','work_finished'].includes(j.status)).length,
    completed:  jobs.filter(j => j.status === 'completed').length,
  };

  const monthOptions = (() => {
    const opts = []; const now = new Date();
    for (let i = -3; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      opts.push({ value: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    return opts;
  })();

  const getPinStatus = (pointId) => {
    if (layerAnnots && annotations.some(a => a.point_id === pointId)) return 'mj-pin-has-annot';
    if (layerNotes  && detail?.notes?.some(n => n.point_id === pointId)) return 'mj-pin-has-note';
    if (layerPhotos && photos.some(p => p.point_id === pointId)) return 'mj-pin-has-photo';
    return 'mj-pin-empty';
  };

  const baseUrl = BASE ? BASE.replace('/api', '') : '';

  return (
    <>
      <style>{STYLES}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div className="md-page-title">Jobs</div>
          <div className="md-page-sub">{jobs.length} total job{jobs.length !== 1 ? 's' : ''}</div>
        </div>
        <button className="md-btn md-btn-ghost" onClick={fetchJobs}><RefreshCw size={13}/> Refresh</button>
      </div>

      <div className="mj-stats-strip">
        {[
          { label:'Total',       val:stats.total,      color:'#38BDF8', border:'rgba(14,165,233,0.20)', bg:'rgba(14,165,233,0.08)' },
          { label:'Scheduled',   val:stats.scheduled,  color:'#38BDF8', border:'rgba(14,165,233,0.20)', bg:'rgba(14,165,233,0.06)' },
          { label:'In Progress', val:stats.inProgress, color:'#FCD34D', border:'rgba(245,158,11,0.20)', bg:'rgba(245,158,11,0.06)' },
          { label:'Completed',   val:stats.completed,  color:'#86EFAC', border:'rgba(34,197,94,0.20)',  bg:'rgba(34,197,94,0.06)'  },
        ].map(s => (
          <div key={s.label} className="mj-stat-chip" style={{ color:s.color, borderColor:s.border, background:s.bg }}>
            <span className="mj-stat-chip-val">{s.val}</span>{s.label}
          </div>
        ))}
      </div>

      <div className="mj-toolbar">
        <div className="mj-search-wrap">
          <Search size={14} className="mj-search-icon"/>
          <input className="mj-search" placeholder="Search jobs…" value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <select className="mj-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {['scheduled','in_progress','completed','cancelled'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
        </select>
        <select className="mj-filter-select" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
          <option value="">All Months</option>
          {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="md-skeleton" style={{ height:300, borderRadius:14 }}/>
      ) : filtered.length === 0 ? (
        <div className="md-empty"><div className="md-empty-icon"><Briefcase size={28}/></div>No jobs found</div>
      ) : (
        <>
          <div className="mj-table-wrap">
            <table className="md-table">
              <thead><tr><th>Site</th><th>Client</th><th>Team</th><th>Date</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {paged.map(job => (
                  <tr key={job.id} style={{ cursor:'pointer' }} onClick={() => openDetail(job)}>
                    <td style={{ fontWeight:600, color:'#E2E8F0' }}>{job.site_name}</td>
                    <td style={{ color:'rgba(255,255,255,0.45)', fontSize:12 }}>{job.client_name}</td>
                    <td style={{ color:'rgba(255,255,255,0.40)', fontSize:12 }}>{job.team_name || '—'}</td>
                    <td style={{ fontFamily:'DM Mono', fontSize:11, color:'rgba(255,255,255,0.45)' }}>
                      {new Date(job.scheduled_date).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                    </td>
                    <td><StatusBadge status={job.status}/></td>
                    <td>
                      <div className="mj-row-actions">
                        <button className="md-btn md-btn-ghost" style={{ padding:'5px 8px' }} onClick={e => { e.stopPropagation(); openDetail(job); }}><Eye size={12}/></button>
                        <button className="md-btn md-btn-danger" style={{ padding:'5px 8px' }} onClick={e => handleDelete(job, e)}><Trash2 size={12}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="mj-pagination">
              <button className="mj-page-btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>‹</button>
              {Array.from({length:totalPages},(_,i)=>i+1)
                .filter(p=>p===1||p===totalPages||Math.abs(p-page)<=1)
                .reduce((acc,p,i,arr)=>{ if(i>0&&arr[i-1]!==p-1) acc.push('...'); acc.push(p); return acc; },[])
                .map((p,i) => p==='...' ? <span key={`e${i}`} className="mj-page-info">…</span> : (
                  <button key={p} className={`mj-page-btn${page===p?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
                ))}
              <button className="mj-page-btn" onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>›</button>
              <span className="mj-page-info">{filtered.length} jobs</span>
            </div>
          )}
        </>
      )}

      {/* ── DETAIL PANEL ── */}
      {selected && (
        <>
          <div className="mj-panel-overlay" onClick={() => setSelected(null)}/>
          <div className="mj-panel">
            <div className="mj-panel-header">
              <div style={{ width:42, height:42, borderRadius:12, background:'rgba(14,165,233,0.12)', display:'flex', alignItems:'center', justifyContent:'center', color:'#38BDF8', flexShrink:0 }}>
                <Briefcase size={18} strokeWidth={1.8}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="mj-panel-title">{detail?.job?.site_name || selected.site_name}</div>
                <div className="mj-panel-sub">
                  {new Date(selected.scheduled_date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
                </div>
              </div>
              <StatusBadge status={selected.status}/>
              <button className="md-modal-close" onClick={() => setSelected(null)}><X size={18}/></button>
            </div>

            <div className="mj-panel-tabs">
              {[
                { id:'info',   label:'Details' },
                { id:'notes',  label:`Notes${detail?.notes?.length ? ` (${detail.notes.length})` : ''}` },
                { id:'visual', label:'Diagram & Photos' },
                { id:'fsr',    label:'FSR' },
              ].map(tab => (
                <button key={tab.id} className={`mj-panel-tab${panelTab===tab.id?' active':''}`} onClick={() => setPanelTab(tab.id)}>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mj-panel-body">
              {detLoading ? (
                [...Array(5)].map((_,i) => <div key={i} className="md-skeleton" style={{ height:16, marginBottom:14, width:`${50+i*10}%` }}/>)
              ) : detail ? (
                <>
                  {/* DETAILS TAB */}
                  {panelTab === 'info' && (
                    <>
                      <div style={{ marginBottom:24 }}>
                        {[
                          { label:'Site',     value:detail.job.site_name,   icon:<MapPin size={13}/> },
                          { label:'Client',   value:detail.job.client_name, icon:<FileText size={13}/> },
                          { label:'Location', value:detail.job.location||'—', icon:<MapPin size={13}/> },
                          { label:'Team',     value:detail.job.team_name||'Unassigned', icon:<UsersRound size={13}/> },
                          { label:'Date',     value:new Date(detail.job.scheduled_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}), icon:<CalendarDays size={13}/> },
                          { label:'Time',     value:detail.job.scheduled_time||'—', icon:<Clock size={13}/> },
                        ].map(({ label, value, icon }) => (
                          <div key={label} className="mj-info-row">
                            <span className="mj-info-label">{label}</span>
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ color:'rgba(14,165,233,0.55)' }}>{icon}</span>{value}
                            </span>
                          </div>
                        ))}
                      </div>

                      {detail.members?.length > 0 && (
                        <div style={{ marginBottom:24 }}>
                          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12 }}>Team Members</div>
                          {detail.members.map(m => (
                            <div key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                              <div style={{ width:30, height:30, borderRadius:8, background:m.role==='lead'?'rgba(245,158,11,0.14)':'rgba(14,165,233,0.10)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, flexShrink:0, color:m.role==='lead'?'#FCD34D':'#38BDF8' }}>
                                {`${m.firstname?.[0]||''}${m.lastname?.[0]||''}`.toUpperCase()}
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:700, color:'#E2E8F0' }}>{m.firstname} {m.lastname}</div>
                                {m.mobile && <div style={{ fontSize:10, color:'rgba(255,255,255,0.28)', fontFamily:'DM Mono' }}>{m.mobile}</div>}
                              </div>
                              <span style={{ fontSize:9, fontWeight:700, color:m.role==='lead'?'#FCD34D':'#38BDF8', fontFamily:'DM Mono', letterSpacing:'0.08em' }}>{m.role.toUpperCase()}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Update Status */}
                      <div>
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:10 }}>Update Status</div>
                        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                          {['scheduled','in_progress','completed','cancelled'].map(s => (
                            <button key={s} className={`mj-status mj-status-${s}`}
                              style={{ cursor:'pointer', border:'1px solid', borderColor:detail.job.status===s?'currentColor':'transparent', opacity:detail.job.status===s?1:0.45, padding:'6px 12px', transition:'all 0.18s' }}
                              onClick={() => updateStatus(s)}>
                              <span className="mj-status-dot"/>{s.replace('_',' ')}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* String Attention Flags */}
                      {jobStrings.length > 0 && (
                        <div className="mj-string-flags-section">
                          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12, display:'flex', alignItems:'center', gap:7 }}>
                            <AlertTriangle size={11} style={{ color:'rgba(239,68,68,0.55)' }}/> String Attention Flags
                          </div>
                          {jobStringFlags.length > 0 && (
                            <div className="mj-flag-warning-bar">
                              <AlertTriangle size={13}/>
                              {jobStringFlags.length} string{jobStringFlags.length > 1 ? 's' : ''} flagged for attention
                            </div>
                          )}
                          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
                            {jobStrings.map(s => {
                              const flagged = jobStringFlags.includes(s.id);
                              return (
                                <button key={s.id} className="mj-string-flag-chip"
                                  onClick={() => toggleStringFlag(s.id)}
                                  style={{ borderColor:flagged?s.color:'rgba(255,255,255,0.12)', background:flagged?`${s.color}22`:'rgba(255,255,255,0.04)', color:flagged?s.color:'rgba(255,255,255,0.38)' }}>
                                  <span style={{ width:9, height:9, borderRadius:2, flexShrink:0, background:flagged?s.color:'rgba(255,255,255,0.18)' }}/>
                                  {flagged && '⚠ '}{s.label}
                                </button>
                              );
                            })}
                          </div>
                          <button className={`md-btn ${flagsSaved ? 'md-btn-ghost' : 'md-btn-primary'}`} style={{ fontSize:11 }} onClick={saveStringFlags} disabled={savingFlags || flagsSaved}>
                            {savingFlags ? <><span className="md-spinner"/> Saving…</> : flagsSaved ? <><CheckCircle2 size={12}/> Flags Saved</> : 'Save Attention Flags'}
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* NOTES TAB */}
                  {panelTab === 'notes' && (
                    detail.notes?.length === 0 ? (
                      <div className="md-empty"><div className="md-empty-icon"><MessageSquare size={24}/></div>No assignment notes</div>
                    ) : detail.notes?.map(note => (
                      <div key={note.id} className="mj-annot-item">
                        <div className="mj-annot-header">
                          <div className="mj-annot-type-dot" style={{ background:note.annotation_type_color||'#94A3B8' }}/>
                          <span className="mj-annot-point">{note.point_label||'General Note'}</span>
                          {note.annotation_type_label && (
                            <span className="md-badge" style={{ background:`${note.annotation_type_color}18`, color:note.annotation_type_color, border:`1px solid ${note.annotation_type_color}33`, fontSize:9, padding:'2px 7px' }}>
                              {note.annotation_type_label}
                            </span>
                          )}
                        </div>
                        <div className="mj-annot-text">{note.note}</div>
                        <div style={{ fontSize:10, color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono' }}>
                          {new Date(note.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                        </div>
                      </div>
                    ))
                  )}

                  {/* VISUAL TAB */}
                  {panelTab === 'visual' && (
                    <>
                      {!detail.diagram ? (
                        <div className="md-empty"><div className="md-empty-icon"><Image size={24}/></div>No diagram for this site</div>
                      ) : (
                        <>
                          <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
                            {[
                              { key:'notes',  label:'📋 Notes',    color:'#F59E0B', state:layerNotes,  set:setLayerNotes  },
                              { key:'photos', label:'📷 Photos',   color:'#22D3A5', state:layerPhotos, set:setLayerPhotos },
                              { key:'annots', label:'🔧 FO Flags', color:'#EF4444', state:layerAnnots, set:setLayerAnnots },
                            ].map(l => (
                              <button key={l.key}
                                style={{ padding:'5px 12px', borderRadius:20, fontSize:10, fontWeight:700, fontFamily:'DM Mono', cursor:'pointer', border:'1px solid', transition:'all 0.18s', background:l.state?`${l.color}18`:'rgba(255,255,255,0.04)', color:l.state?l.color:'rgba(255,255,255,0.35)', borderColor:l.state?`${l.color}40`:'rgba(255,255,255,0.08)', opacity:l.state?1:0.45 }}
                                onClick={() => l.set(s => !s)}>{l.label}</button>
                            ))}
                          </div>

                          {/* Diagram with polygon string overlays */}
                          <div className="mj-diagram-wrap">
                            <img src={`${baseUrl}${detail.diagram.diagram_url}`} alt="Site diagram" className="mj-diagram-img"/>

                            {/* SVG polygon overlays */}
                            <svg className="mj-svg-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
                              {jobStrings.map(s => {
                                const pts = Array.isArray(s.points) ? s.points : [];
                                if (pts.length < 3) return null;
                                const isFlagged = jobStringFlags.includes(s.id);
                                return (
                                  <polygon
                                    key={s.id}
                                    className={isFlagged ? 'mj-string-flagged-polygon' : ''}
                                    points={svgPts(pts)}
                                    fill={isFlagged ? `${s.color}35` : `${s.color}18`}
                                    stroke={s.color}
                                    strokeWidth={isFlagged ? '0.7' : '0.4'}
                                    strokeOpacity={isFlagged ? '0.95' : '0.60'}
                                  />
                                );
                              })}
                            </svg>

                            {/* Centroid labels for strings */}
                            {jobStrings.map(s => {
                              const pts = Array.isArray(s.points) ? s.points : [];
                              if (pts.length < 3) return null;
                              const isFlagged = jobStringFlags.includes(s.id);
                              const c = centroid(pts);
                              return (
                                <span key={`lbl-${s.id}`} className="mj-string-centroid-label"
                                  style={{ left:`${c.x}%`, top:`${c.y}%`, color:s.color }}>
                                  {isFlagged ? '⚠ ' : ''}{s.label}
                                </span>
                              );
                            })}

                            {/* Pins */}
                            {detail.diagram.points?.map((pin, idx) => (
                              <div key={pin.id} className="mj-diagram-pin" style={{ left:`${pin.x_percent}%`, top:`${pin.y_percent}%` }}>
                                <div className={`mj-pin-dot ${getPinStatus(pin.id)}`}>{idx + 1}</div>
                                {pin.label && <div className="mj-pin-label-tag">{pin.label}</div>}
                              </div>
                            ))}
                          </div>

                          {/* Flagged strings banner */}
                          {jobStringFlags.length > 0 && (
                            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 13px', borderRadius:9, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.20)', marginBottom:16, fontSize:11, fontWeight:700, fontFamily:'DM Mono', color:'#FCA5A5' }}>
                              <AlertTriangle size={13}/> {jobStringFlags.length} string{jobStringFlags.length>1?'s':''} require attention on this job
                            </div>
                          )}

                          {/* Legend */}
                          <div style={{ display:'flex', gap:14, fontSize:10, color:'rgba(255,255,255,0.30)', fontFamily:'DM Mono', marginBottom:20, flexWrap:'wrap' }}>
                            {[{color:'#F59E0B',label:'Has Note'},{color:'#22D3A5',label:'Has Photo'},{color:'#EF4444',label:'FO Flag'},{color:'rgba(255,255,255,0.28)',label:'No data'}].map(l => (
                              <span key={l.label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                                <span style={{ width:8, height:8, borderRadius:'50%', background:l.color, display:'inline-block' }}/>{l.label}
                              </span>
                            ))}
                          </div>

                          {!photosLoaded ? (
                            <div className="md-skeleton" style={{ height:280, borderRadius:12 }}/>
                          ) : (
                            <StreetViewViewer photos={photos} baseUrl={baseUrl}/>
                          )}

                          {annotations.length > 0 && (
                            <>
                              <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginBottom:12, marginTop:20 }}>
                                FO Field Annotations ({annotations.length})
                              </div>
                              {annotations.map(a => (
                                <div key={a.id} className="mj-annot-item">
                                  <div className="mj-annot-header">
                                    <div className="mj-annot-type-dot" style={{ background:a.annotation_type_color||'#94A3B8' }}/>
                                    <span className="mj-annot-point">{a.point_label}</span>
                                    <span className="mj-annot-fo">{a.fo_name}</span>
                                  </div>
                                  {a.note && <div className="mj-annot-text">{a.note}</div>}
                                  {a.photo_url && <img src={`${baseUrl}${a.photo_url}`} alt="Annotation" className="mj-annot-photo"/>}
                                </div>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* FSR TAB */}
                  {panelTab === 'fsr' && (
                    detail.fsr ? (
                      <div className="mj-fsr-card">
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(34,197,94,0.60)', fontFamily:'DM Mono', marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
                          <CheckCircle2 size={12}/> FSR Submitted
                        </div>
                        {[
                          { label:'Panels Cleaned', value:detail.fsr.panels_cleaned??'—' },
                          { label:'Submitted By',   value:detail.fsr.submitted_by_name||'—' },
                          { label:'Submitted At',   value:new Date(detail.fsr.submitted_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'}) },
                          { label:'Cable Condition',value:detail.fsr.cable_condition||'—' },
                          { label:'Cable Quantity', value:detail.fsr.cable_quantity||'—' },
                          { label:'Panel Brand',    value:detail.fsr.panel_brand||'—' },
                          { label:'Alarm Code',     value:detail.fsr.alarm_code||'—' },
                          { label:'Shading Details',value:detail.fsr.shading_details||'—' },
                        ].map(({ label, value }) => (
                          <div key={label} className="mj-info-row">
                            <span className="mj-info-label">{label}</span>
                            <span style={{ color:'#E2E8F0', fontWeight:600 }}>{value}</span>
                          </div>
                        ))}
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', margin:'16px 0 8px' }}>Inspection Results</div>
                        <div className="mj-fsr-grid">
                          <FsrBool label="Panel Damage"      value={detail.fsr.panel_damage      === 'Yes'} />
                          <FsrBool label="Inverter Alarm"    value={detail.fsr.inverter_alarm    === 'Yes'} />
                          <FsrBool label="Potential Shading" value={detail.fsr.potential_shading === 'Yes'} />
                          <FsrBool label="Rusting"           value={detail.fsr.rusting}           />
                          <FsrBool label="Bird Dropping"     value={detail.fsr.bird_dropping}     />
                          <FsrBool label="Moss & Debris"     value={detail.fsr.mos_and_debris}    />
                          <FsrBool label="Earthing"          value={detail.fsr.earthing}          />
                        </div>
                        {detail.fsr.observations && <div className="mj-info-row"><span className="mj-info-label">Observations</span><span style={{ color:'rgba(255,255,255,0.50)', fontSize:12, lineHeight:1.55 }}>{detail.fsr.observations}</span></div>}
                        {detail.fsr.work_done      && <div className="mj-info-row"><span className="mj-info-label">Work Done</span><span style={{ color:'rgba(255,255,255,0.50)', fontSize:12, lineHeight:1.55 }}>{detail.fsr.work_done}</span></div>}
                        {detail.fsr.signature_url && (
                          <>
                            <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.22)', fontFamily:'DM Mono', marginTop:16, marginBottom:8 }}>Signature</div>
                            <img src={`${baseUrl}${detail.fsr.signature_url}`} alt="Signature" className="mj-fsr-sig"/>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="md-empty"><div className="md-empty-icon"><FileText size={24}/></div>FSR not submitted yet</div>
                    )
                  )}
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MideastJobs;